import { useEffect } from 'react';
import {
  CapacityManagementRootResponse,
  InvoicesParamsForTso,
  InvoicesForTsoResponse,
} from 'src/apis/capacity-management/types';
import { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import urlTemplate from 'url-template';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: InvoicesParamsForTso;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: InvoicesParamsForTso;
};

export function useInvoices({ capacityManagementRoot, params }: HookConfig) {
  const invoices = useAxios(
    (axios, signal, { rootData, params }: RequestOptions) => {
      const link = rootData._links?.invoicesV2 ?? rootData._links?.invoices;
      if (!link) throw new Forbidden();

      const url = urlTemplate.parse(link.href).expand({});

      return axios.request<InvoicesForTsoResponse>({
        signal,
        url,
        params,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        if (error instanceof Forbidden) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const options = { params, rootData: capacityManagementRoot.response.data };

    invoices.execute(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return invoices;
}

export type InvoicesRequest = ReturnType<typeof useInvoices>;
